import { useRef, useCallback } from "react";
import { activePack } from "@/components/cef/packs";

/* Procedural menu audio v2 — all synthesized with Web Audio (no assets, CEF-safe).
   Buses: master → music / sfx. The sfx bus runs through a style filter that
   workshop sound packs recolor (digital = crisp/bright, lofi = dusty lowpass,
   analog = warm rolled-off). Default = flat.
   Music: generative dark-synth bed in A minor @ 72bpm — Am → F → C/E → G,
   detuned pad + pulsing sub bass + sparse pluck melody + bar-change noise swells.
   Context unlocks on the first user gesture (title screen "press any key"). */

const SFX_STYLES = {
  default: { type: "lowpass", freq: 20000, q: 0.7, gain: 1.0 },
  digital: { type: "highpass", freq: 260, q: 1.2, gain: 1.12 },
  lofi:    { type: "lowpass", freq: 1500, q: 2.2, gain: 0.8 },
  analog:  { type: "lowpass", freq: 5200, q: 1.6, gain: 0.94 },
};

const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);
const BPM = 72;
const EIGHTH = 60 / BPM / 2;           // ~0.417s
const BAR = EIGHTH * 8;                // one chord per bar
const PROG = [                          // Am — F — C/E — G
  { pad: [57, 60, 64], bass: 33 },
  { pad: [53, 57, 60], bass: 29 },
  { pad: [55, 60, 64], bass: 36 },
  { pad: [55, 59, 62], bass: 31 },
];
const PLUCK_SCALE = [69, 72, 74, 76, 79, 81, 84, 88]; // A minor pentatonic-ish

export function useMenuAudio() {
  const ctxRef = useRef(null);
  const busRef = useRef(null);
  const volRef = useRef({ master: 0.8, music: 0.7, sfx: 0.9 });
  const ambRef = useRef(null);
  const musRef = useRef(null);
  const muffleRef = useRef(null);
  const lastTick = useRef(0);

  const ensure = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const master = ctx.createGain(); master.gain.value = volRef.current.master; master.connect(ctx.destination);
      const muffle = ctx.createBiquadFilter(); muffle.type = "lowpass"; muffle.frequency.value = 20000; muffle.connect(master);
      const music = ctx.createGain(); music.gain.value = volRef.current.music; music.connect(muffle);
      const sfx = ctx.createGain(); sfx.gain.value = volRef.current.sfx;
      const styleFx = ctx.createBiquadFilter();
      const st = SFX_STYLES[activePack("sound_pack")?.pack?.sound?.sfx || "default"] || SFX_STYLES.default;
      styleFx.type = st.type; styleFx.frequency.value = st.freq; styleFx.Q.value = st.q;
      sfx.gain.value = volRef.current.sfx * st.gain;
      sfx.connect(styleFx); styleFx.connect(master);
      busRef.current = { master, music, sfx };
      muffleRef.current = muffle;
      ctxRef.current = ctx;
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
    return ctxRef.current;
  }, []);

  const noiseBuf = (ctx, secs = 1) => {
    const buf = ctx.createBuffer(1, ctx.sampleRate * secs, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  };

  /* atmospheric crime weather — the menu's siren/heli layer follows the server's
     live crime mood (0 calm .. 1 warzone), fed from /nc/menu/world */
  const crimeRef = useRef(0);
  const setCrime = useCallback((v) => { crimeRef.current = Math.max(0, Math.min(1, v || 0)); }, []);

  const env = (ctx, node, peak, a, d, when = 0) => {
    const g = ctx.createGain();
    const t = ctx.currentTime + when;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + a + d);
    node.connect(g);
    return g;
  };

  /* ---------------- SFX ---------------- */

  const tick = useCallback(() => { // soft hover blip, rate-limited
    const now = performance.now();
    if (now - lastTick.current < 40) return;
    lastTick.current = now;
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = 1560;
    const o2 = ctx.createOscillator(); o2.type = "square"; o2.frequency.value = 3120;
    const g2 = ctx.createGain(); g2.gain.value = 0.15; o2.connect(g2);
    env(ctx, o, 0.05, 0.002, 0.045).connect(busRef.current.sfx);
    env(ctx, g2, 0.02, 0.001, 0.02).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 0.07); o2.start(); o2.stop(ctx.currentTime + 0.03);
  }, [ensure]);

  const click = useCallback(() => { // heavy metallic engage
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "triangle";
    o.frequency.setValueAtTime(340, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + 0.1);
    env(ctx, o, 0.24, 0.003, 0.14).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 0.17);
    const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.06);
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 2500; bp.Q.value = 1.4;
    n.connect(bp);
    env(ctx, bp, 0.13, 0.001, 0.05).connect(busRef.current.sfx);
    n.start(); n.stop(ctx.currentTime + 0.06);
    const ping = ctx.createOscillator(); ping.type = "sine"; ping.frequency.value = 2210;
    env(ctx, ping, 0.04, 0.002, 0.18).connect(busRef.current.sfx);
    ping.start(); ping.stop(ctx.currentTime + 0.2);
  }, [ensure]);

  const back = useCallback(() => {
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(120, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(62, ctx.currentTime + 0.16);
    env(ctx, o, 0.3, 0.004, 0.2).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 0.25);
  }, [ensure]);

  const riser = useCallback((secs = 1.1, peak = 0.1) => { // cinematic sweep up
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "sawtooth";
    o.frequency.setValueAtTime(80, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(720, ctx.currentTime + secs);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass";
    lp.frequency.setValueAtTime(300, ctx.currentTime);
    lp.frequency.exponentialRampToValueAtTime(4200, ctx.currentTime + secs);
    o.connect(lp);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + secs * 0.85);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + secs + 0.15);
    lp.connect(g); g.connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + secs + 0.2);
    const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, secs); n.loop = false;
    const hp = ctx.createBiquadFilter(); hp.type = "highpass";
    hp.frequency.setValueAtTime(400, ctx.currentTime);
    hp.frequency.exponentialRampToValueAtTime(6000, ctx.currentTime + secs);
    n.connect(hp);
    env(ctx, hp, 0.05, secs * 0.8, 0.2).connect(busRef.current.sfx);
    n.start(); n.stop(ctx.currentTime + secs);
  }, [ensure]);

  const start = useCallback(() => { // title → menu: riser into sub impact + shimmer
    const ctx = ensure(); if (!ctx) return;
    riser(1.0, 0.09);
    const hitAt = 1.0;
    const o = ctx.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(50, ctx.currentTime + hitAt);
    o.frequency.exponentialRampToValueAtTime(28, ctx.currentTime + hitAt + 0.8);
    env(ctx, o, 0.7, 0.005, 0.95, hitAt).connect(busRef.current.sfx);
    o.start(ctx.currentTime + hitAt); o.stop(ctx.currentTime + hitAt + 1.0);
    const shimmer = ctx.createOscillator(); shimmer.type = "sine"; shimmer.frequency.value = 1567; // G6
    env(ctx, shimmer, 0.05, 0.02, 0.9, hitAt + 0.05).connect(busRef.current.sfx);
    shimmer.start(ctx.currentTime + hitAt + 0.05); shimmer.stop(ctx.currentTime + hitAt + 1.0);
  }, [ensure, riser]);

  const connect = useCallback(() => { // relay clunk ×2 + confirm tone
    const ctx = ensure(); if (!ctx) return;
    [0, 0.15].forEach((when) => {
      const o = ctx.createOscillator(); o.type = "square"; o.frequency.value = when ? 110 : 165;
      env(ctx, o, 0.26, 0.002, 0.07, when).connect(busRef.current.sfx);
      o.start(ctx.currentTime + when); o.stop(ctx.currentTime + when + 0.09);
      const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.04);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 950;
      n.connect(bp);
      env(ctx, bp, 0.14, 0.001, 0.04, when).connect(busRef.current.sfx);
      n.start(ctx.currentTime + when); n.stop(ctx.currentTime + when + 0.05);
    });
    const ok = ctx.createOscillator(); ok.type = "sine"; ok.frequency.value = 880;
    env(ctx, ok, 0.06, 0.01, 0.4, 0.4).connect(busRef.current.sfx);
    ok.start(ctx.currentTime + 0.4); ok.stop(ctx.currentTime + 0.85);
  }, [ensure]);

  const deny = useCallback(() => { // low buzz for unavailable actions
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "square"; o.frequency.value = 140;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 500;
    o.connect(lp);
    env(ctx, lp, 0.09, 0.004, 0.16).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 0.2);
  }, [ensure]);

  const whoosh = useCallback((peak = 0.12) => { // cinematic air sweep for logo cards / panels
    const ctx = ensure(); if (!ctx) return;
    const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.6);
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 0.8;
    bp.frequency.setValueAtTime(500, ctx.currentTime);
    bp.frequency.exponentialRampToValueAtTime(2600, ctx.currentTime + 0.28);
    bp.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.55);
    n.connect(bp);
    env(ctx, bp, peak, 0.06, 0.5).connect(busRef.current.sfx);
    n.start(); n.stop(ctx.currentTime + 0.6);
  }, [ensure]);

  const boom = useCallback(() => { // title-landing sub drop + metallic tail
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(70, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(26, ctx.currentTime + 1.1);
    env(ctx, o, 0.75, 0.008, 1.2).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 1.3);
    const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.4);
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 900;
    n.connect(lp);
    env(ctx, lp, 0.18, 0.002, 0.35).connect(busRef.current.sfx);
    n.start(); n.stop(ctx.currentTime + 0.4);
    const ring = ctx.createOscillator(); ring.type = "sine"; ring.frequency.value = 1046;
    env(ctx, ring, 0.04, 0.01, 1.4, 0.06).connect(busRef.current.sfx);
    ring.start(ctx.currentTime + 0.06); ring.stop(ctx.currentTime + 1.5);
  }, [ensure]);

  /* ---------------- boot-sequence SFX (matrix/computing theatre) ---------------- */

  const crtOn = useCallback(() => { // CRT power-on: mains zap + static crackle + settling tube hum
    const ctx = ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = "sawtooth";
    o.frequency.setValueAtTime(1200, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.35);
    env(ctx, o, 0.16, 0.004, 0.4).connect(busRef.current.sfx);
    o.start(); o.stop(ctx.currentTime + 0.45);
    const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.1);
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 3000;
    n.connect(hp);
    env(ctx, hp, 0.08, 0.001, 0.08).connect(busRef.current.sfx);
    n.start(); n.stop(ctx.currentTime + 0.1);
    const hum = ctx.createOscillator(); hum.type = "sine"; hum.frequency.value = 60;
    env(ctx, hum, 0.045, 0.05, 0.9, 0.15).connect(busRef.current.sfx);
    hum.start(ctx.currentTime + 0.15); hum.stop(ctx.currentTime + 1.1);
  }, [ensure]);

  const teletype = useCallback(() => { // matrix keystroke cluster (2-4 clicks, random pitch)
    const ctx = ensure(); if (!ctx) return;
    const n = 2 + ((Math.random() * 3) | 0);
    for (let i = 0; i < n; i++) {
      const when = i * 0.045;
      const o = ctx.createOscillator(); o.type = "square"; o.frequency.value = 1800 + Math.random() * 1400;
      env(ctx, o, 0.035, 0.001, 0.025, when).connect(busRef.current.sfx);
      o.start(ctx.currentTime + when); o.stop(ctx.currentTime + when + 0.03);
    }
  }, [ensure]);

  const lineOk = useCallback(() => { // soft two-tone confirm as each boot line passes
    const ctx = ensure(); if (!ctx) return;
    [660, 990].forEach((f, i) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = f;
      env(ctx, o, 0.05, 0.003, 0.09, i * 0.07).connect(busRef.current.sfx);
      o.start(ctx.currentTime + i * 0.07); o.stop(ctx.currentTime + i * 0.07 + 0.11);
    });
  }, [ensure]);

  const dataBurst = useCallback((steps = 6) => { // rapid digital processing ladder (descending)
    const ctx = ensure(); if (!ctx) return;
    for (let i = 0; i < steps; i++) {
      const when = i * 0.08;
      const o = ctx.createOscillator(); o.type = "square"; o.frequency.value = 2400 - i * 260;
      env(ctx, o, 0.03, 0.001, 0.05, when).connect(busRef.current.sfx);
      o.start(ctx.currentTime + when); o.stop(ctx.currentTime + when + 0.06);
    }
  }, [ensure]);

  /* ---------------- ambience: city rumble + rain + distant siren ---------------- */

  const startAmbience = useCallback(() => {
    const ctx = ensure(); if (!ctx || ambRef.current) return;
    const rumble = ctx.createBufferSource(); rumble.buffer = noiseBuf(ctx, 2); rumble.loop = true;
    const rlp = ctx.createBiquadFilter(); rlp.type = "lowpass"; rlp.frequency.value = 90; // deep, smooth — no hiss
    rumble.connect(rlp);
    const rg = ctx.createGain(); rg.gain.value = 0.08;
    rlp.connect(rg); rg.connect(busRef.current.music);
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.06;
    const lg = ctx.createGain(); lg.gain.value = 0.025;
    lfo.connect(lg); lg.connect(rg.gain);
    // no broadband rain/static — a soft slow air pad instead
    const air = ctx.createBufferSource(); air.buffer = noiseBuf(ctx, 3); air.loop = true;
    const alp = ctx.createBiquadFilter(); alp.type = "lowpass"; alp.frequency.value = 420;
    air.connect(alp);
    const airg = ctx.createGain(); airg.gain.value = 0.012;
    alp.connect(airg); airg.connect(busRef.current.music);
    const siren = () => { // distant siren wail — closer and more frequent as crime climbs
      if (!ambRef.current) return;
      const hot = crimeRef.current > 0.55;
      const o = ctx.createOscillator(); o.type = "sine";
      const t = ctx.currentTime;
      o.frequency.setValueAtTime(660, t);
      for (let i = 0; i < 6; i++) o.frequency.linearRampToValueAtTime(i % 2 ? 660 : 880, t + i * 0.9 + 0.9);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(hot ? 0.017 : 0.006, t + 1.2);
      g.gain.linearRampToValueAtTime(0.0001, t + 5.4);
      o.connect(g); g.connect(busRef.current.music);
      o.start(t); o.stop(t + 5.6);
      ambRef.current.sirenTimer = setTimeout(siren, hot ? 5000 + Math.random() * 5000 : 16000 + Math.random() * 14000);
    };
    const heli = () => { // warzone nights: a chopper circles overhead
      if (!ambRef.current) return;
      if (crimeRef.current >= 0.72) {
        const t = ctx.currentTime;
        const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 2); n.loop = true;
        const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 240;
        const chop = ctx.createOscillator(); chop.type = "square"; chop.frequency.value = 11;
        const chopG = ctx.createGain(); chopG.gain.value = 0.5;
        const g = ctx.createGain(); g.gain.value = 0;
        chop.connect(chopG); chopG.connect(g.gain);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(0.02, t + 1.5);
        g.gain.setValueAtTime(0.02, t + 6);
        g.gain.linearRampToValueAtTime(0.0001, t + 9);
        n.connect(lp); lp.connect(g); g.connect(busRef.current.music);
        n.start(t); n.stop(t + 9.2); chop.start(t); chop.stop(t + 9.2);
      }
      ambRef.current.heliTimer = setTimeout(heli, 14000 + Math.random() * 12000);
    };
    rumble.start(); air.start(); lfo.start();
    ambRef.current = { rumble, air, lfo, sirenTimer: setTimeout(siren, 9000), heliTimer: setTimeout(heli, 12000) };
  }, [ensure]);

  /* ---------------- generative music bed ---------------- */

  const startMusic = useCallback(() => {
    const ctx = ensure(); if (!ctx || musRef.current) return;
    const bus = busRef.current.music;
    const state = { bar: 0, nextBarAt: ctx.currentTime + 0.1, next8thAt: ctx.currentTime + 0.1, eighth: 0, timer: null };
    musRef.current = state;

    const padChord = (midis, at) => {
      midis.forEach((m) => {
        [-6, 5].forEach((cents) => {
          const o = ctx.createOscillator(); o.type = "sawtooth";
          o.frequency.value = mtof(m); o.detune.value = cents;
          const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 620; lp.Q.value = 0.4;
          o.connect(lp);
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, at);
          g.gain.linearRampToValueAtTime(0.024, at + 1.1);
          g.gain.setValueAtTime(0.024, at + BAR - 0.9);
          g.gain.linearRampToValueAtTime(0.0001, at + BAR + 0.3);
          lp.connect(g); g.connect(bus);
          o.start(at); o.stop(at + BAR + 0.4);
        });
      });
    };

    const bass8th = (midi, at, accent) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = mtof(midi);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(accent ? 0.16 : 0.08, at + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, at + EIGHTH * 0.92);
      o.connect(g); g.connect(bus);
      o.start(at); o.stop(at + EIGHTH);
    };

    const pluck = (midi, at) => {
      const o = ctx.createOscillator(); o.type = "triangle"; o.frequency.value = mtof(midi);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.075, at + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.5);
      o.connect(g);
      const dl = ctx.createDelay(0.6); dl.delayTime.value = EIGHTH * 0.75;
      const fb = ctx.createGain(); fb.gain.value = 0.25;
      g.connect(bus); g.connect(dl); dl.connect(fb); fb.connect(dl);
      const wet = ctx.createGain(); wet.gain.value = 0.3; dl.connect(wet); wet.connect(bus);
      o.start(at); o.stop(at + 0.6);
    };

    const swell = (at) => {
      const n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 2);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.setValueAtTime(240, at);
      bp.frequency.exponentialRampToValueAtTime(880, at + 2.2); bp.Q.value = 1.1;
      n.connect(bp);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.linearRampToValueAtTime(0.008, at + 1.4);
      g.gain.linearRampToValueAtTime(0.0001, at + 2.6);
      bp.connect(g); g.connect(bus);
      n.start(at); n.stop(at + 2.7);
    };

    state.timer = setInterval(() => {
      const ahead = ctx.currentTime + 0.25;
      while (state.nextBarAt < ahead) {
        const chord = PROG[state.bar % PROG.length];
        const at = state.nextBarAt;
        padChord(chord.pad, at);
        if (state.bar % 2 === 0) swell(at + BAR - 2.4);
        for (let e = 0; e < 8; e++) bass8th(chord.bass, at + e * EIGHTH, e === 0);
        state.nextBarAt += BAR;
        state.bar += 1;
      }
      while (state.next8thAt < ahead) {
        const at = state.next8thAt;
        if (Math.random() < 0.3 && state.eighth % 2 === 1) {
          const chord = PROG[state.bar % PROG.length];
          const pool = PLUCK_SCALE.concat(chord.pad.map((m) => m + 12));
          pluck(pool[(Math.random() * pool.length) | 0], at);
        }
        state.next8thAt += EIGHTH;
        state.eighth += 1;
      }
    }, 60);
  }, [ensure]);

  const setMuffle = useCallback((on) => { // travelling: 800Hz low-pass + duck
    const ctx = ctxRef.current;
    if (!ctx || !muffleRef.current) return;
    muffleRef.current.frequency.cancelScheduledValues(ctx.currentTime);
    muffleRef.current.frequency.setTargetAtTime(on ? 700 : 20000, ctx.currentTime, 0.12);
    busRef.current.music.gain.setTargetAtTime(on ? volRef.current.music * 0.25 : volRef.current.music, ctx.currentTime, 0.15);
  }, []);

  const setVolumes = useCallback((v) => {
    volRef.current = { ...volRef.current, ...v };
    const b = busRef.current;
    if (!b) return;
    b.master.gain.value = volRef.current.master;
    b.music.gain.value = volRef.current.music;
    b.sfx.gain.value = volRef.current.sfx;
  }, []);

  return { ensure, tick, click, back, start, riser, connect, deny, whoosh, boom, crtOn, teletype, lineOk, dataBurst, startAmbience, startMusic, setMuffle, setVolumes, setCrime, volRef };
}
